import { getBasePath } from '@/tenant/basePath.ts'

/**
 * Registers the service worker for the studio in the address bar.
 *
 * Scope is `/s/<slug>/`, so two studios installed on one phone get separate
 * registrations, separate caches and separate install identities. On the
 * legacy development routes nothing is registered at all: a root-scoped
 * worker would serve every studio and defeat the isolation.
 */
export async function registerTenantServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  // The worker is emitted by the production build only. Registering in dev
  // would ask the dev server for a file it does not have, and the browser
  // would log a MIME-type error on every page load.
  if (!import.meta.env.PROD) return null

  const base = getBasePath()
  if (base === '') return null

  try {
    return await navigator.serviceWorker.register(`${base}/sw.js`, { scope: `${base}/`, updateViaCache: 'none' })
  } catch {
    // An unavailable worker must never stop the app from booting.
    return null
  }
}

/**
 * The studio's worker once it is active — what `pushManager.subscribe` needs.
 *
 * `register()` resolves as soon as the worker is *installing*. Subscribing then
 * fails with "no active Service Worker", which is exactly what the first
 * attempt on a fresh device did: the owner's first tap on «Включить
 * уведомления», and a customer's first «Напомнить за сутки», both failed.
 * `ready` waits for activation; the page is inside the worker's scope, so it
 * does resolve.
 */
export async function activeTenantServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  const registration = await registerTenantServiceWorker()
  if (registration === null) return null
  if (registration.active !== null) return registration
  return navigator.serviceWorker.ready
}

/** Asks the worker to drop everything it cached for this studio. */
export async function clearPrivateCaches(): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  const registration = await navigator.serviceWorker.getRegistration()
  registration?.active?.postMessage({ type: 'clear-private-cache' })
}
